<template>
  <aside>
    <div v-if="shouldShow" class="sidebar__overlay" @click="onClose"></div>
    <Transition name="slide">
      <div v-if="shouldShow" ref="target" class="sidebar z-global-sidebar">
        <div class="sidebar__close-button-area">
          <button aria-label="Close sidebar" type="button" @click="onClose">
            <img alt="" src="@/assets/icon/close/default.svg" />
          </button>
        </div>
        <div class="sidebar__inner">
          <!-- Main Navigation -->
          <ul class="sidebar__nav">
            <!-- Search -->
            <li class="sidebar__nav-item">
              <button class="sidebar__nav-link" type="button" @click="onOpenSearchModal">
                <img alt="" height="24" src="@/assets/icon/search/default.svg" width="24" />
                <span>Search</span>
              </button>
            </li>
            <!-- Accordion Nav Items -->
            <li v-for="item in accordionNavItems" :key="item.id" class="sidebar__nav-item">
              <button class="sidebar__nav-link sidebar__nav-link--accordion" type="button" :aria-expanded="openAccordionId === item.id" @click="toggleAccordion(item.id)">
                <div class="sidebar__nav-link-left">
                  <img alt="" height="24" width="24" :src="item.icon" />
                  <span>{{ item.label }}</span>
                </div>
                <span class="sidebar__nav-chevron" :class="{ 'is-open': openAccordionId === item.id }" />
              </button>
              <!-- Destinations Content -->
              <Transition v-if="item.id === 'destinations'" name="accordion">
                <div v-show="openAccordionId === 'destinations'">
                  <div class="sidebar__accordion-content">
                    <div class="sidebar__destination-list">
                      <NuxtLink
                        v-for="destination in destinationsWithUrls"
                        :key="destination.id"
                        class="sidebar__destination-item"
                        :class="{ 'is-selected': isSelectedDestination(destination.id) }"
                        :to="destination.link"
                        @click="onClickNavItem('destinations', destination.titleEn)"
                      >
                        <OptimizedImage :alt="destination.titleEn" :class="['sidebar__destination-img']" :image-url="destination.imageUrl" :optimized-image-options="{ width: SIDEBAR_THUMB_WIDTH }" />
                        <span class="sidebar__destination-title">{{ destination.titleEn }}</span>
                      </NuxtLink>
                    </div>
                  </div>
                  <button class="sidebar__view-more" type="button" @click="onOpenSearchWithDestinations">
                    <span>View more</span>
                    <span class="sidebar__view-more-arrow" />
                  </button>
                </div>
              </Transition>
              <!-- Journey Content -->
              <Transition v-if="item.id === 'journey'" name="accordion">
                <div v-show="openAccordionId === 'journey'" class="sidebar__accordion-content sidebar__accordion-content--journey">
                  <!-- Short-Stay Itineraries -->
                  <div class="sidebar__sub-accordion">
                    <button class="sidebar__sub-accordion-button" type="button" :aria-expanded="openSubAccordionIds.includes('short-stay')" @click="toggleSubAccordion('short-stay')">
                      <span>Short-Stay Itineraries</span>
                      <span class="sidebar__nav-chevron" :class="{ 'is-open': openSubAccordionIds.includes('short-stay') }" />
                    </button>
                    <Transition name="accordion">
                      <div v-show="openSubAccordionIds.includes('short-stay')" class="sidebar__sub-accordion-content sidebar__sub-accordion-content--with-line">
                        <a v-for="area in shortStayAreas" :key="area.id" class="sidebar__area-link" :href="getShortStayLink(area.id)" @click="onClickNavItem('journeys', area.label)">
                          {{ area.label }}
                        </a>
                      </div>
                    </Transition>
                  </div>
                  <!-- Multi-Day Itineraries -->
                  <div class="sidebar__sub-accordion">
                    <button class="sidebar__sub-accordion-button" type="button" :aria-expanded="openSubAccordionIds.includes('multi-day')" @click="toggleSubAccordion('multi-day')">
                      <span>Multi-Day Itineraries</span>
                      <span class="sidebar__nav-chevron" :class="{ 'is-open': openSubAccordionIds.includes('multi-day') }" />
                    </button>
                    <Transition name="accordion">
                      <div v-show="openSubAccordionIds.includes('multi-day')" class="sidebar__sub-accordion-content sidebar__sub-accordion-content--with-line">
                        <NuxtLink
                          v-for="itinerary in multiDayItineraries"
                          :key="itinerary.id"
                          class="sidebar__itinerary-link"
                          :to="itinerary.link"
                          @click="onClickNavItem('journeys', itinerary.titleEn)"
                        >
                          {{ itinerary.titleEn }}
                        </NuxtLink>
                      </div>
                    </Transition>
                  </div>
                  <a class="sidebar__view-more" :href="SHORT_STAY_BASE_URL" @click="onClickNavItem('journeys', 'view_more')">
                    <span>View more</span>
                    <span class="sidebar__view-more-arrow" />
                  </a>
                </div>
              </Transition>
              <!-- Stays Content -->
              <Transition v-if="item.id === 'stays'" name="accordion">
                <div v-show="openAccordionId === 'stays'">
                  <div class="sidebar__accordion-content">
                    <div class="sidebar__stays-list">
                      <NuxtLink v-for="group in curatedStaysTagGroups" :key="group.name" class="sidebar__stays-link" :to="group.link" @click="onClickNavItem('stays', group.name)">
                        {{ group.name }}
                      </NuxtLink>
                    </div>
                  </div>
                  <NuxtLink class="sidebar__view-more" :to="CURATED_STAYS_VIEW_MORE_URL" @click="onClickNavItem('stays', 'view_more')">
                    <span>View more</span>
                    <span class="sidebar__view-more-arrow" />
                  </NuxtLink>
                </div>
              </Transition>
              <!-- Experiences Content -->
              <Transition v-if="item.id === 'experiences'" name="accordion">
                <div v-show="openAccordionId === 'experiences'">
                  <div class="sidebar__accordion-content">
                    <div class="sidebar__experiences-list">
                      <NuxtLink v-for="theme in themesWithUrls" :key="theme.id" class="sidebar__experiences-item" :to="theme.link" @click="onClickNavItem('experiences', theme.titleEn)">
                        <OptimizedImage :alt="theme.titleEn" :class="['sidebar__experiences-img']" :image-url="theme.imageUrl" :optimized-image-options="{ width: SIDEBAR_THUMB_WIDTH }" />
                        <span class="sidebar__experiences-title">{{ theme.titleEn }}</span>
                      </NuxtLink>
                    </div>
                  </div>
                  <button class="sidebar__view-more" type="button" @click="onOpenSearchWithThemes">
                    <span>View more</span>
                    <span class="sidebar__view-more-arrow" />
                  </button>
                </div>
              </Transition>
            </li>
            <!-- Contact us -->
            <li class="sidebar__nav-item">
              <NuxtLink class="sidebar__nav-link" to="/contact/" @click="onClose">
                <img alt="" height="24" src="@/assets/icon/mail/default.svg" width="24" />
                <span>Contact us</span>
              </NuxtLink>
            </li>
          </ul>
          <hr class="border-b border-solid text-gray1" />
          <a :href="ABOUT_LP_URL" @click="onClose">
            <AtomsTextWithArrow class="flex h-10 items-center" :style="{ '--color': '#333', '--hover': '#333' }">
              <p class="font-Spectral text-base font-light tracking-normal text-stone-700">About us</p>
            </AtomsTextWithArrow>
          </a>
          <NuxtLink to="/stories/" @click="onClose">
            <AtomsTextWithArrow class="flex h-10 items-center" :style="{ '--color': '#333', '--hover': '#333' }">
              <p class="font-Spectral text-base font-light tracking-normal text-stone-700">Wabunka Stories</p>
            </AtomsTextWithArrow>
          </NuxtLink>
          <a rel="noopener noreferrer" target="_blank" :href="GIFT_LP_URL" @click="onClose">
            <AtomsTextWithArrow class="flex h-10 items-center" :style="{ '--color': '#333', '--hover': '#333' }">
              <p class="font-Spectral text-base font-light tracking-normal text-stone-700">Purchase a gift card</p>
            </AtomsTextWithArrow>
          </a>
          <NuxtLink to="/faq/" @click="onClose">
            <AtomsTextWithArrow class="flex h-10 items-center" :style="{ '--color': '#333', '--hover': '#333' }">
              <p class="font-Spectral text-base font-light tracking-normal text-stone-700">FAQ</p>
            </AtomsTextWithArrow>
          </NuxtLink>
          <NuxtLink to="/lp/partner-with-us/" :prefetch="false" @click="onClose">
            <AtomsTextWithArrow class="flex h-10 items-center" :style="{ '--color': '#333', '--hover': '#333' }">
              <p class="font-Spectral text-base font-light tracking-normal text-stone-700">Partner with us</p>
            </AtomsTextWithArrow>
          </NuxtLink>
          <a rel="noopener noreferrer" target="_blank" :href="ENTERPRISE_LP_URL" @click="onClose">
            <AtomsTextWithArrow class="flex h-10 items-center" :style="{ '--color': '#333', '--hover': '#333' }">
              <p class="font-Spectral text-base font-light tracking-normal text-stone-700">For teams and large groups</p>
            </AtomsTextWithArrow>
          </a>
          <div class="mt-[10px]">
            <div class="followus">
              <p class="font-Spectral text-base font-light tracking-normal text-stone-700">Follow us</p>
              <div class="flex items-center">
                <a class="mr-4" href="https://www.instagram.com/wabunka.japan/" target="_blank">
                  <img alt="instagram icon" src="@/assets/icon/instagram/default.svg" width="20" />
                </a>
                <a class="mr-4" href="https://www.facebook.com/wabunka.japan/?ref=page_internal" target="_blank">
                  <img alt="facebook icon" src="@/assets/icon/facebook/default.svg" width="20" />
                </a>
                <a href="https://www.youtube.com/@WabunkaJapan" rel="noopener noreferrer" target="_blank">
                  <img alt="youtube icon" src="@/assets/icon/youtube/default.svg" width="20" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </aside>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';

import curatedStaysIcon from '@/assets/icon/curated_stays/default.svg';
import destinationIcon from '@/assets/icon/destination/default.svg';
import experiencesIcon from '@/assets/icon/experiences/default.svg';
import journeyIcon from '@/assets/icon/journey/default.svg';
import { useAsyncWabunkaTags } from '@/composables/models/wabunka/useAsyncWabunkaTags';
import { useCurrentTabIndex } from '@/composables/ui/useCurrentTabIndex';
import { useGlobalModal } from '@/composables/ui/useGlobalModal';
import { OptimizedImage } from '@/wabunka-src/common/components';
import { useNavigationFeatured } from '@/wabunka-src/common/features/Featured/composables/query/useNavigationFeatured';
import { generateDetailPageLink, getBackgroundImageUrl, type MinimumFeaturedViewModel } from '@/wabunka-src/common/features/Featured/models/minimunFeatured/minimumFeatured';
import { sendGAWabunkaClickHeaderNavEvent, type HeaderNavGenre } from '@/wabunka-src/common/utils/googleAnalytics/events/clickHeaderNav';
import { ABOUT_LP_URL } from '@/wabunka-src/constants/aboutLp';
import { ENTERPRISE_LP_URL } from '@/wabunka-src/constants/enterpriseLp';
import { GIFT_LP_URL } from '@/wabunka-src/constants/giftLp';
import {
  SHORT_STAY_AREAS,
  NAVIGATION_MULTI_DAY_LIMIT,
  SHORT_STAY_BASE_URL,
  CURATED_STAYS_THEME_SLUG,
  CURATED_STAYS_VIEW_MORE_URL,
  CURATED_STAYS_EXCLUDED_GROUPS,
  SEARCH_TAB_INDEX,
} from '@/wabunka-src/constants/navigation';
import { ITINERARY_TAG_DAYS_THRESHOLD } from '@/wabunka-src/features/Itinerary/constants/itineraryTagLabels';
import { useKeywordSearchModal } from '@/wabunka-src/features/Search/composables/general/useKeywordSearchModal';

interface Props {
  shouldShow: boolean;
  onClose: () => void;
}

const props = withDefaults(defineProps<Props>(), {
  shouldShow: false,
  onClose: () => {},
});

const { open } = useGlobalModal('searchExperiencesModal');
const { open: openKeywordSearchModal } = useKeywordSearchModal();
const { updateTabIndex } = useCurrentTabIndex('searchExperiencesModalTabIndex');
const { destinations, itineraries, themes } = useNavigationFeatured();
const { destinationTagGroups } = await useAsyncWabunkaTags({ throwOnError: false });

const accordionNavItems = [
  { id: 'destinations', label: 'Destinations', icon: destinationIcon },
  { id: 'journey', label: 'Journey', icon: journeyIcon },
  { id: 'stays', label: 'Stays', icon: curatedStaysIcon },
  { id: 'experiences', label: 'Experiences', icon: experiencesIcon },
];

const openAccordionId = ref<string | null>(null);
const openSubAccordionIds = ref<string[]>([]);

// 最適化は OptimizedImage 側で行うため、ここでは生の背景画像 URL を渡す。
const SIDEBAR_THUMB_WIDTH = 560;

const destinationsWithUrls = computed(() =>
  destinations.value.map((destination) => ({
    ...destination,
    link: `/${destination.id}/`,
    imageUrl: getBackgroundImageUrl(destination),
  })),
);

const curatedStaysTagGroups = computed(() => {
  return Object.entries(destinationTagGroups.value)
    .filter(([name]) => !CURATED_STAYS_EXCLUDED_GROUPS.includes(name))
    .map(([name, tags]) => {
      const destinations = tags.map((tag) => tag.webPath.replace(/^\//, '')).join(',');
      return {
        name,
        link: `/s/experiences/?destinations=${destinations}&themes=${CURATED_STAYS_THEME_SLUG}`,
      };
    });
});

const shortStayAreas = SHORT_STAY_AREAS;

/** Multi-Day旅程（日数 > 3、または日数未設定は除外） */
const multiDayItineraries = computed(() =>
  itineraries.value
    .filter((itinerary) => {
      const days = itinerary.itineraryTagSettings?.days;
      return days !== undefined && days > ITINERARY_TAG_DAYS_THRESHOLD;
    })
    .slice(0, NAVIGATION_MULTI_DAY_LIMIT)
    .map((itinerary) => ({
      ...itinerary,
      link: generateDetailPageLink(itinerary),
    })),
);

const toggleAccordion = (id: string) => {
  openAccordionId.value = openAccordionId.value === id ? null : id;
  // 親アコーディオンを閉じたらサブアコーディオンもリセット
  if (openAccordionId.value !== id) {
    openSubAccordionIds.value = [];
  }
};

const toggleSubAccordion = (id: string) => {
  if (openSubAccordionIds.value.includes(id)) {
    openSubAccordionIds.value = openSubAccordionIds.value.filter((subId) => subId !== id);
  } else {
    openSubAccordionIds.value = [...openSubAccordionIds.value, id];
  }
};

/** Short-Stayエリアのリンクを生成 */
const getShortStayLink = (areaId: string) => {
  return `${SHORT_STAY_BASE_URL}#${areaId}`;
};

const onClickNavItem = (genre: HeaderNavGenre, itemName: string) => {
  sendGAWabunkaClickHeaderNavEvent(genre, itemName, 'sidebar');
  props.onClose();
};

const onOpenSearchModal = () => {
  props.onClose();
  openKeywordSearchModal();
};

const onOpenSearchWithDestinations = () => {
  sendGAWabunkaClickHeaderNavEvent('destinations', 'view_more', 'sidebar');
  props.onClose();
  updateTabIndex(SEARCH_TAB_INDEX.DESTINATIONS);
  open();
};

const onOpenSearchWithThemes = () => {
  sendGAWabunkaClickHeaderNavEvent('experiences', 'view_more', 'sidebar');
  props.onClose();
  updateTabIndex(SEARCH_TAB_INDEX.THEMES);
  open();
};

/** テーマリスト（Experiences用） */
const themesWithUrls = computed(() =>
  themes.value.map((theme: MinimumFeaturedViewModel) => ({
    ...theme,
    link: `/japan/${theme.id}/`,
    imageUrl: getBackgroundImageUrl(theme),
  })),
);

const target = ref<HTMLElement | null>(null);
const route = useRoute();

const isSelectedDestination = (destinationId: string) => {
  const currentPath = route.path;
  return currentPath === `/${destinationId}/` || currentPath === `/${destinationId}`;
};

onClickOutside(target, props.onClose);
</script>

<style lang="scss" scoped>
button {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  color: inherit;
}

%sidebar-link-style {
  font-family: 'Spectral', serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  color: #1a150e;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
  &:hover {
    color: #b8860b;
  }
}

.sidebar {
  position: fixed;
  top: 0;
  right: 0;
  width: 90%;
  max-width: 400px;
  height: 100vh;
  background: #fff;
  overflow-y: auto;
  z-index: 102;
  &__close-button-area {
    position: absolute;
    top: 24px;
    right: 24px;
    cursor: pointer;
  }
  &__inner {
    padding: 76px 24px 40px;
  }
  &__nav {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  &__nav-item {
    margin-bottom: 0;
  }
  &__nav-link {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 0;
    cursor: pointer;
    text-decoration: none;
    color: #171717;
    font-family: 'Spectral', serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0;
    &--accordion {
      width: 100%;
      justify-content: space-between;
    }
    &-left {
      display: flex;
      align-items: center;
      gap: 12px;
    }
  }
  &__nav-chevron {
    width: 8px;
    height: 8px;
    margin-right: 4px;
    border-top: solid 1px #333;
    border-right: solid 1px #333;
    transform: rotate(45deg);
    transition: transform 0.3s ease;
    &.is-open {
      transform: rotate(135deg);
    }
  }
  &__accordion-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-left: 20px;
    margin-left: 12px;
    overflow: hidden;
    &--journey {
      gap: 0;
    }
  }
  &__destination-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-left: 20px;
    margin-left: -20px;
    border-left: 1px solid #dad6d0;
  }
  &__destination-item {
    display: flex;
    align-items: center;
    gap: 12px;
    text-decoration: none;
    color: #171717;
    &:hover {
      .sidebar__destination-title {
        color: #b8860b;
      }
    }
  }
  // OptimizedImage の内部 img へ届かせるため :deep を使う
  :deep(.sidebar__destination-img) {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 0;
  }
  &__destination-title {
    @extend %sidebar-link-style;
  }
  &__view-more {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    width: 100%;
    padding: 8px 0;
    background: none;
    border: none;
    cursor: pointer;
    font-family: 'Spectral', serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0;
    vertical-align: middle;
    color: #171717;
    transition: color 0.3s ease;
    &:hover {
      color: #b8860b;
      .sidebar__view-more-arrow {
        border-color: #b8860b;
      }
    }
  }
  &__view-more-arrow {
    width: 6px;
    height: 6px;
    margin-right: 4px;
    border-top: solid 1px #333;
    border-right: solid 1px #333;
    transform: rotate(45deg);
    transition: border-color 0.3s ease;
  }
  &__sub-accordion-button {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 8px 8px 8px 16px;
    font-family: 'Spectral', serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0;
    color: #1a150e;
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 1px;
      background-color: #dad6d0;
    }
  }
  &__sub-accordion-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-left: 24px;
    margin: 8px 0;
    overflow: hidden;
    &--with-line {
      position: relative;
      padding-left: 16px;
      margin-left: 8px;
      border-left: 1px solid #dad6d0;
    }
  }
  &__area-link {
    @extend %sidebar-link-style;
    display: block;
  }
  &__itinerary-link {
    @extend %sidebar-link-style;
    display: block;
  }
  &__stays-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-left: 20px;
    margin-left: -20px;
    border-left: 1px solid #dad6d0;
  }
  &__stays-link {
    @extend %sidebar-link-style;
    display: block;
    padding: 4px 0;
  }
  &__stays-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-left: 20px;
    margin-left: -20px;
    border-left: 1px solid #dad6d0;
  }
  &__stays-link {
    display: block;
    padding: 4px 0;
    font-family: 'Spectral', serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0;
    color: #171717;
    text-decoration: none;
    &:hover {
      color: #b8860b;
    }
  }
  &__experiences-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-left: 20px;
    margin-left: -20px;
    border-left: 1px solid #dad6d0;
  }
  &__experiences-item {
    display: flex;
    align-items: center;
    gap: 12px;
    text-decoration: none;
    color: #171717;
    &:hover {
      .sidebar__experiences-title {
        color: #b8860b;
      }
    }
  }
  // OptimizedImage の内部 img へ届かせるため :deep を使う
  :deep(.sidebar__experiences-img) {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 0;
  }
  &__experiences-title {
    @extend %sidebar-link-style;
  }
  &__overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(8px);
    z-index: 101;
  }
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0s ease;
}
.slide-enter-to,
.slide-leave-to {
  transform: translateX(100%);
  transition: all 0.4s ease-in 0s;
}
.slide-enter-to {
  transform: translateX(0%);
}

.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.3s ease;
}
.accordion-enter-from,
.accordion-leave-to {
  opacity: 0;
  max-height: 0;
}
.accordion-enter-to,
.accordion-leave-from {
  opacity: 1;
  max-height: 500px;
}

hr {
  margin: 32px 0;
}

.followus {
  display: flex;
  margin: 0 0 24px;
  justify-content: space-between;
  align-items: center;
  a {
    text-decoration: none;
  }
}
</style>
